import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { listThreads, createThread, postMessage } from './api';
import { COPY } from './copy';
import MessageList from './MessageList';
import MessageComposer from './MessageComposer';
import RequestPromptModal from './RequestPromptModal';

// Imperatively-controllable panel. Parent uses ref.openPrompt() to launch
// the request modal from a button rendered elsewhere (e.g. the report form's
// existing button row).
//
// viewer: { type: 'specialist' | 'gp' | 'admin' }
// issue:  { id, is_open, ... }
// mode:   'inline' (default) | 'admin-readonly'
const RequestThreadPanel = forwardRef(({ viewer, issue, mode = 'inline', onStateChange }, ref) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);

  const refresh = async () => {
    try {
      const data = await listThreads(issue.id);
      setThreads(data || []);
    } catch (err) {
      if (err.response?.status !== 403) {
        setError('Could not load request threads.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [issue.id]);

  const specialistToGpThread = threads.find(
    (t) => t.direction === 'specialist_to_gp'
  );

  const canSpecialistOpen =
    viewer.type === 'specialist' &&
    issue.is_open &&
    !specialistToGpThread &&
    mode === 'inline';

  useImperativeHandle(ref, () => ({
    openPrompt: () => setPromptOpen(true),
    canOpen: () => canSpecialistOpen,
    hasOpenSpecialistToGpThread: () => !!specialistToGpThread,
  }), [canSpecialistOpen, specialistToGpThread]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        canOpen: canSpecialistOpen,
        hasOpenSpecialistToGpThread: !!specialistToGpThread,
      });
    }
  }, [canSpecialistOpen, specialistToGpThread, onStateChange]);

  const handleSubmitNewRequest = async ({ body }) => {
    const created = await createThread(issue.id, { body });
    setThreads((prev) => [...prev, created]);
  };

  const handleSpecialistFollowup = async ({ body, files }) => {
    if (!specialistToGpThread) return;
    const { request_message: newMessage, thread: updatedThread } = await postMessage(
      issue.id,
      specialistToGpThread.id,
      { body, files }
    );
    setThreads((prev) => prev.map((t) => (
      t.id === specialistToGpThread.id
        ? { ...t, messages: [...t.messages, newMessage], state: updatedThread.state, last_activity_at: updatedThread.last_activity_at }
        : t
    )));
  };

  const lastMessageAuthorRole = specialistToGpThread?.messages?.[specialistToGpThread.messages.length - 1]?.author?.role;
  const awaitingSpecialistFollowup = lastMessageAuthorRole === 'gp';
  const showSpecialistFollowupComposer =
    viewer.type === 'specialist' &&
    specialistToGpThread &&
    specialistToGpThread.state === 'open' &&
    awaitingSpecialistFollowup &&
    mode === 'inline';

  if (loading) return null;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="flex flex-col gap-y-3">
      {specialistToGpThread && (
        <div>
          <div
            className="font-semibold uppercase text-sm text-white px-3 py-2 rounded-t"
            style={{ backgroundColor: COPY.hex.alertRed }}
          >
            {COPY.sections.additionalInformationRequested}
          </div>
          <div className="border border-t-0 border-gray-200 rounded-b p-3 bg-slate-50">
            <MessageList messages={specialistToGpThread.messages} />
            {showSpecialistFollowupComposer && (
              <MessageComposer onSubmit={handleSpecialistFollowup} placeholder="Ask the GP another question" />
            )}
          </div>
        </div>
      )}

      <RequestPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        onSubmit={handleSubmitNewRequest}
      />
    </div>
  );
});

export default RequestThreadPanel;
